import { Command as CommandPrimitive } from 'cmdk';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Check } from 'lucide-react';

import { cn } from '@/slw/shared/lib/utils';
import { Skeleton } from '@/slw/shared/ui/skeleton';
import { CommandGroup, CommandInput, CommandItem, CommandList } from '@/slw/shared/ui/command';
import { SelectOption } from '@/slw/shared/types/common.type';
import { useDebounce } from '@/slw/shared/hooks/use-debounce';

type AutoCompleteProps = {
  options: SelectOption[];
  emptyMessage?: string;
  value?: SelectOption;
  onValueChange?: (value: SelectOption) => void;
  onSearchAsync?: (value: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  shouldFilterAsync?: boolean;
  error?: boolean;
  className?: string;
};

export const AutoComplete = ({
  options,
  emptyMessage = 'No results.',
  placeholder = 'Find something',
  value,
  onValueChange,
  onSearchAsync,
  disabled,
  isLoading = false,
  shouldFilterAsync = false,
  className,
  error,
}: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setOpen] = useState(false);
  const [selected, setSelected] = useState<SelectOption | undefined>(value);
  const [inputValue, setInputValue] = useState<string>(value?.label || '');
  const debouncedSearchTerm = useDebounce(inputValue, 300); // Trì hoãn 300ms

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;

      if (!input) {
        return;
      }

      if (!isOpen) {
        setOpen(true);
      }

      if (event.key === 'Enter' && input.value !== '') {
        const optionToSelect = options.find((option) => option.label === input.value);

        if (optionToSelect) {
          setSelected(optionToSelect);
          onValueChange?.(optionToSelect);
        }
      }

      if (event.key === 'Escape') {
        input.blur();
      }
    },
    [isOpen, options, onValueChange],
  );

  const handleBlur = useCallback(() => {
    setOpen(false);
    setInputValue(selected?.label || '');
  }, [selected]);

  const handleSelectOption = useCallback(
    (selectedOption: SelectOption) => {
      setInputValue(selectedOption.label);

      setSelected(selectedOption);
      onValueChange?.(selectedOption);
      setTimeout(() => {
        inputRef?.current?.blur();
      }, 0);
    },
    [onValueChange],
  );

  useEffect(() => {
    if (shouldFilterAsync) {
      onSearchAsync?.(debouncedSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, shouldFilterAsync]);

  useEffect(() => {
    if (value?.value !== selected?.value) {
      setSelected(value);
      setInputValue(value?.label || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <CommandPrimitive onKeyDown={handleKeyDown} shouldFilter={!shouldFilterAsync}>
      <div className={cn('w-auto min-w-24', !!error && '[&_div]:border-error', className)}>
        <CommandInput
          ref={inputRef}
          value={inputValue}
          onValueChange={setInputValue}
          onBlur={handleBlur}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
      <div className='relative mt-1'>
        <div
          className={cn(
            'animate-in fade-in-0 zoom-in-95 absolute top-0 z-10 w-full rounded-xl bg-white outline-none',
            isOpen ? 'block' : 'hidden',
          )}
        >
          <CommandList className='rounded-lg ring-1 ring-slate-200'>
            {isLoading ? (
              <CommandPrimitive.Loading>
                <div className='p-1'>
                  <Skeleton className='h-8 w-full' />
                </div>
              </CommandPrimitive.Loading>
            ) : null}
            {options.length > 0 && !isLoading ? (
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected?.value === option.value;

                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onSelect={() => handleSelectOption(option)}
                      className={cn('flex w-full items-center gap-2', !isSelected ? 'pl-8' : null)}
                    >
                      {isSelected ? <Check className='w-4' /> : null}
                      {option.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
            {!isLoading ? (
              <CommandPrimitive.Empty className='select-none rounded-sm px-2 py-3 text-center text-sm'>
                {emptyMessage}
              </CommandPrimitive.Empty>
            ) : null}
          </CommandList>
        </div>
      </div>
    </CommandPrimitive>
  );
};
